import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import TypingText from "@/components/TypingText";
import GrabadorPanel from "@/components/GrabadorPanel";
import MicTooltip from "@/components/MicTooltip";
import { useMomentos } from "@/context/MomentosContext";

const PASOS_PROC = [
  "Escuchando lo que contaste",
  "Entendiendo tu rol",
  "Identificando tus herramientas",
  "Detectando dónde se te va el tiempo",
];

export default function Inicio() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const { pedir, hayMomento } = useMomentos();
  const [b2, setB2] = useState(false);
  const [tip, setTip] = useState(false);
  const [introListo, setIntroListo] = useState(false);
  const [modoTexto, setModoTexto] = useState(false);
  const [modoAudio, setModoAudio] = useState(false);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [tick, setTick] = useState(0);
  const [demorado, setDemorado] = useState(false);
  const [repregunta, setRepregunta] = useState("");
  const [dichos, setDichos] = useState([]); // lo que la persona ya contó (se ve arriba, la caja queda vacía)
  const timers = useRef([]);

  const mensaje2 = `Mandame un audio contándome cómo es un día tuyo de trabajo.\nComo si se lo contaras a alguien que no sabe nada de lo que hacés.\nCuanto más detallado, más se va a parecer todo lo que sigue a tu negocio real.`;

  const limpiar = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => limpiar(), []);

  // Momento de conciencia (máquina de escribir): lo primero que ve, antes de pedirle que cuente el día.
  useEffect(() => { pedir("m1"); }, [pedir]);

  const subir = async (fn, onFail) => {
    setIntroListo(true); setB2(true);
    setProcesando(true); setTick(0); setDemorado(false); setEnviando(true);
    const tid = setInterval(() => setTick((t) => Math.min(t + 1, 3)), 1500);
    const slow = setTimeout(() => setDemorado(true), 90000);
    try {
      const res = await fn();
      clearInterval(tid); clearTimeout(slow);
      if (res && res.suficiente === false) {
        const msg = res.repregunta || "Con eso no me alcanza. Contame un poco más.";
        setProcesando(false);
        setEnviando(false);
        setModoTexto(true);
        setRepregunta(msg);
        return;
      }
      nav("/perfil");
    } catch (err) {
      clearInterval(tid); clearTimeout(slow);
      setProcesando(false);
      setEnviando(false);
      onFail && onFail();
      toast.error(errText(err));
    }
  };

  const enviarAudio = async (blob, done) => {
    setTranscribiendo(true);
    try {
      const ext = ((blob.type && blob.type.split("/")[1]) || "webm").split(";")[0];
      const fd = new FormData();
      fd.append("file", blob, `audio.${ext}`);
      const { data } = await api.post("/transcribir", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTexto((prev) => (prev ? prev.trim() + " " : "") + data.texto);
      setIntroListo(true);
      setModoAudio(false);
      setModoTexto(true);
      if ((user?.mic_tip_count || 0) < 3) {
        setTip(true);
        api.post("/mic-tip-visto").catch(() => {});
        setUser && setUser((u) => (u ? { ...u, mic_tip_count: (u.mic_tip_count || 0) + 1 } : u));
      }
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setTranscribiendo(false);
      done && done();
    }
  };

  const enviarTexto = () => {
    const t = texto.trim();
    if (!t) return;
    setRepregunta("");
    // La caja se vacía al mandar; lo que contó queda visible arriba. Al backend le mandamos todo
    // lo dicho hasta ahora (acumulado) para que no pierda contexto entre repreguntas.
    setDichos((d) => [...d, t]);
    setTexto("");
    subir(
      async () => {
        const todo = [...dichos, t].join("\n\n");
        const { data } = await api.post("/perfil/texto", { texto: todo });
        return data;
      },
      () => { setTexto(t); setDichos((d) => d.slice(0, -1)); }
    );
  };

  // Mientras el momento de conciencia (m1) está en pantalla, /inicio no monta nada detrás.
  if (hayMomento) return <div className="min-h-screen bg-negro" data-testid="inicio-esperando-momento" />;

  if (procesando) {
    return (
      <div className="min-h-screen bg-negro flex items-center justify-center px-6">
        <div className="space-y-5">
          {PASOS_PROC.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0.15 }} animate={{ opacity: i <= tick ? 1 : 0.2 }} transition={{ duration: 0.4 }} className="flex items-center gap-4">
              <span className="w-5 h-5 flex items-center justify-center">
                {i < tick || (i === tick && tick === 3) ? <Check size={18} className="text-dorado" /> : i === tick ? <span className="w-2 h-2 rounded-full bg-dorado animate-pulse" /> : <span className="w-1.5 h-1.5 rounded-full bg-borde" />}
              </span>
              <span className="text-lg" style={{ color: i <= tick ? "#F5F0E8" : "#555" }}>{p}</span>
            </motion.div>
          ))}
          {demorado && (
            <div className="pt-6">
              <p className="text-gris text-sm mb-4">Está tardando más de lo normal. Puede ser la conexión.</p>
              <div className="flex gap-3">
                <button className="btn-fantasma !py-2 !px-4" onClick={() => { setProcesando(false); setEnviando(false); }} data-testid="proc-reintentar">PROBAR DE NUEVO</button>
                <button className="btn-fantasma !py-2 !px-4" onClick={() => { setProcesando(false); setEnviando(false); setIntroListo(true); setModoTexto(true); }} data-testid="proc-escribir">ESCRIBIRLO</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-negro flex items-center justify-center px-6">
      <div className="w-full max-w-2xl">
        {!repregunta && (
          <>
            <div className="text-crema text-xl leading-relaxed mb-6">
              {introListo ? (
                <span style={{ whiteSpace: "pre-wrap" }}>{`${user?.nombre}, antes de arrancar quiero pedirte una sola cosa.`}</span>
              ) : (
                <TypingText text={`${user?.nombre}, antes de arrancar quiero pedirte una sola cosa.`} onDone={() => timers.current.push(setTimeout(() => setB2(true), 1000))} />
              )}
            </div>
            {(b2 || introListo) && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-crema text-xl leading-relaxed mb-5">
                {introListo ? (
                  <span style={{ whiteSpace: "pre-wrap" }}>{mensaje2}</span>
                ) : (
                  <TypingText text={mensaje2} onDone={() => setIntroListo(true)} />
                )}
              </motion.div>
            )}
            {(b2 || introListo) && (
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className="flex items-start gap-2 text-gris text-sm leading-relaxed mb-16"
                data-testid="inicio-refuerzo"
              >
                <span className="text-dorado mt-0.5 shrink-0">·</span>
                <span>Cuando me contás todo esto, lo que lográs es ordenar tus procesos y poder automatizarlos.</span>
              </motion.p>
            )}
          </>
        )}

        <AnimatePresence>
          {b2 && !modoTexto && !modoAudio && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.5 }} className="flex flex-col items-center gap-6">
              <GrabadorPanel variant="grande" onEnviar={enviarAudio} enviando={transcribiendo} />
              <button className="text-sm text-gris hover:text-dorado transition-colors" onClick={() => { setIntroListo(true); setModoTexto(true); }} data-testid="prefiero-escribir">prefiero escribirlo</button>
            </motion.div>
          )}

          {b2 && modoAudio && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-6">
              {texto.trim() && (
                <div
                  className="w-full max-w-xl rounded-xl p-4"
                  style={{ border: "1px solid rgba(201,168,76,0.45)", background: "rgba(201,168,76,0.05)" }}
                  data-testid="audio-texto-guardado"
                >
                  <div className="mono-label text-dorado mb-2">LO QUE ESCRIBISTE ESTÁ GUARDADO</div>
                  <p className="text-gris text-sm leading-relaxed" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{texto}</p>
                  <p className="text-gris text-xs mt-2">El audio se suma a lo que ya tenías.</p>
                </div>
              )}
              <GrabadorPanel variant="grande" autoStart onEnviar={enviarAudio} enviando={transcribiendo} onCancelar={() => { setModoAudio(false); setModoTexto(true); }} />
              <button className="text-sm text-gris hover:text-dorado transition-colors" onClick={() => { setModoAudio(false); setModoTexto(true); }} data-testid="volver-a-escribir">volver a lo que escribí</button>
            </motion.div>
          )}

          {b2 && modoTexto && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {(dichos.length > 0 || repregunta) && (
                <div className="space-y-3 mb-2" data-testid="arranque-conversacion">
                  {dichos.map((d, i) => (
                    <div key={i} className="flex justify-end" data-testid={`arranque-dicho-${i}`}>
                      <div
                        className="max-w-[85%] rounded-2xl rounded-br-sm px-4 py-3 text-sm leading-relaxed"
                        style={{ background: "#1A1A1A", border: "1px solid #3C3C3C", color: "#E9E1D2", whiteSpace: "pre-wrap" }}
                      >
                        {d}
                      </div>
                    </div>
                  ))}
                  {repregunta && (
                    <div className="flex justify-start" data-testid="perfil-repregunta">
                      <div className="max-w-[92%] flex gap-2.5">
                        <span className="text-dorado mt-1 shrink-0">·</span>
                        <p className="text-crema leading-relaxed">{repregunta}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="relative">
                <MicTooltip show={tip} onClose={() => setTip(false)} arrow="down" align="left" />
                <textarea className="input-dark min-h-[180px]" placeholder="Contame cómo es un día tuyo de trabajo…" value={texto} onChange={(e) => { setTexto(e.target.value); if (tip) setTip(false); }} data-testid="texto-perfil" />
              </div>
              <div className="flex items-center gap-4">
                <button className="btn-dorado" onClick={enviarTexto} disabled={!texto.trim() || enviando} data-testid="enviar-texto">MANDAR</button>
                <button className="text-sm text-gris hover:text-dorado transition-colors" onClick={() => { setModoTexto(false); setModoAudio(true); }} data-testid="mejor-grabo-audio">mejor grabo un audio</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
